import React, { useState } from "react";
import { Button, Modal, Input, message, Spin } from "antd";
import { FilePdfOutlined, MailOutlined } from "@ant-design/icons";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import type { InvoiceType } from "../../features/participants/types/participantsTypes";
import { post } from "../services/httpClient";
//import logo from "../../../public/report-logo-small.png";
const logo = "/report-logo-small.png"

interface Props {
    invoice: InvoiceType;
}

export interface SendInvoiceEmailRequest {
    invoiceId: number | string;
    pdfBase64: string;
    emailTo: string;
    fullName: string | undefined;
    totalAmount: number;
    invoiceDate: Date;
    currencyISOCode: string | undefined;
}

export interface SendInvoiceEmailResponse {
    message: string;
}

export const sendInvoiceEmail = async (data: SendInvoiceEmailRequest): Promise<SendInvoiceEmailResponse> => {
    // POST to your API endpoint
    return await post<SendInvoiceEmailResponse>("/invoice/SendInvoice", data);
};

const InvoicePrint: React.FC<Props> = ({ invoice }) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const handleGeneratePdfBase64 = async (): Promise<string> => {
        const doc = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "a4",
            precision: 2, // ✅ reduce internal float precision
            compress: true, // ✅ enable built-in compression
        });

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        // === Load and compress logo (convert to JPEG) ===
        const imageData = await fetch(logo)
            .then((r) => r.blob())
            .then(
                (blob) =>
                    new Promise<string>((resolve) => {
                        const reader = new FileReader();
                        const img = new Image();
                        reader.onload = () => {
                            img.src = reader.result as string;
                            img.onload = () => {
                                const canvas = document.createElement("canvas");
                                const ctx = canvas.getContext("2d")!;
                                const targetWidth = img.width * 0.5; // reduce by 50%
                                const targetHeight = img.height * 0.5;
                                canvas.width = targetWidth;
                                canvas.height = targetHeight;
                                ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
                                // ✅ convert to JPEG with slight compression
                                resolve(canvas.toDataURL("image/jpeg", 0.8));
                            };
                        };
                        reader.readAsDataURL(blob);
                    })
            );

        // === Watermark ===
        const addBackgroundLogo = () => {
            const logoWidth = 80;
            const logoHeight = 80;
            const logoX = (pageWidth - logoWidth) / 2;
            const logoY = (pageHeight - logoHeight) / 2;

            doc.saveGraphicsState();
            doc.setGState(new (doc as any).GState({ opacity: 0.08 }));
            doc.addImage(imageData, "JPEG", logoX, logoY, logoWidth, logoHeight); // ✅ JPEG instead of PNG
            doc.restoreGraphicsState();
        };

        // === Header Logo ===
        const addHeaderLogo = () => {
            doc.addImage(imageData, "JPEG", 14, 20, 14, 14); // ✅ JPEG again
        };

        // === Header Info ===
        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.text(`Invoice #${invoice.id}`, 34, 27);

        doc.setFontSize(12);
        doc.setFont("helvetica", "italic");
        doc.text(`${invoice.name}`, 34, 33);

        // === Status Box ===
        const status = (invoice.status ?? "UNPAID").toUpperCase();
        const stampColor: [number, number, number] =
            status === "PAID"
                ? [0, 128, 0]
                : status === "CANCELLED"
                    ? [255, 0, 0]
                    : [255, 0, 0];

        const stampFontSize = 16;
        doc.setFontSize(stampFontSize);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...stampColor);

        const textWidth = doc.getTextWidth(status);
        const rightMargin = 15;
        const paddingX = 6,
            paddingY = 4;
        const textHeight = stampFontSize * 0.5;
        const rectX = pageWidth - rightMargin - (textWidth + paddingX * 2);
        const textX = rectX + (textWidth + paddingX * 2) / 2;

        doc.text(status, textX, 25, { align: "center" });
        doc.setDrawColor(...stampColor);
        doc.setLineWidth(1);
        (doc as any).setLineDashPattern([2, 2], 0);
        doc.rect(
            rectX,
            25 - textHeight - paddingY / 2,
            textWidth + paddingX * 2,
            textHeight + paddingY
        );
        doc.setTextColor(0, 0, 0);
        (doc as any).setLineDashPattern([], 0);

        // === Line ===
        doc.setLineWidth(0.4);
        doc.setDrawColor(200, 200, 200);
        doc.line(14, 38, 196, 38);

        // === Invoice Info ===
        const leftX = 14,
            rightX = 105;
        let y = 47;
        doc.setFontSize(10);
        doc.text(`Plan: ${invoice.planName}`, leftX, y);
        doc.text(`Group: ${invoice.groupName}`, rightX, y);

        y += 8;
        doc.text(
            `Invoice Date: ${dayjs(invoice.invoiceDate).format("DD-MM-YYYY")}`,
            leftX,
            y
        );
        doc.text(
            `Due Date: ${dayjs(invoice.dueDate).format("DD-MM-YYYY")}`,
            rightX,
            y
        );

        y += 8;
        doc.text(`Currency: ${invoice.currencyISOCode}`, leftX, y);

        // === Table ===
        autoTable(doc, {
            startY: y + 15,
            head: [["Name", "Age", "Gender", "Category", "Amount"]],
            body:
                invoice.lineItems?.map((item) => [
                    item.name ?? "",
                    item.age ?? "",
                    item.gender ?? "",
                    item.category ?? "",
                    `${item.amount?.toLocaleString() ?? "0"} ${item.currencyISOCode ?? ""
                    }`,
                ]) ?? [],
            foot: [
                [
                    "",
                    "",
                    "",
                    "Total",
                    `${invoice.totalAmount.toLocaleString()} ${invoice.currencyISOCode}`,
                ],
            ],
            styles: { fontSize: 10 },
            headStyles: { fillColor: [41, 128, 185], textColor: 255 },
            footStyles: {
                fillColor: [41, 128, 185],
                textColor: 255,
                fontStyle: "bold",
            },
        });

        // === Notes ===
        let finalY = (doc as any).lastAutoTable.finalY + 10;
        if (invoice.notes) {
            doc.setFont("helvetica", "italic");
            doc.text(`Note: ${invoice.notes}`, leftX, finalY);
        }

        // === Add logo & footer per page ===
        const pageCount = doc.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            addBackgroundLogo();
            addHeaderLogo();

            // === Footer Line ===
            doc.setDrawColor(180, 180, 180);
            doc.setLineWidth(0.3);
            doc.line(14, pageHeight - 18, pageWidth - 14, pageHeight - 18); // ✅ footer line

            // === Footer Text ===
            doc.setFontSize(9);
            doc.setFont("helvetica", "italic");
            doc.setTextColor(100);

            doc.text("TPA Insurance Company Ltd.", 14, pageHeight - 14);
            doc.text(
                "123 Business Street, Iraq | +964 780-666-9919",
                14,
                pageHeight - 10
            );
            doc.text(
                "https://insurance-staging.altaifgroup.com/",
                pageWidth - 14,
                pageHeight - 10,
                { align: "right" }
            );
        }

        // ✅ Return PDF as Base64 (no file saved)
        return doc.output("datauristring").split(",")[1];
    };

    const handleSendEmail = async () => {
        if (!email) {
            message.warning("Please enter an email address");
            return;
        }

        try {
            setLoading(true);
            const pdfBase64 = await handleGeneratePdfBase64();
            const res = await sendInvoiceEmail({
                invoiceId: invoice.id,
                pdfBase64,
                emailTo: email,
                fullName: invoice.name,
                invoiceDate: invoice.invoiceDate,
                totalAmount: invoice.totalAmount,
                currencyISOCode: invoice.currencyISOCode,
            });

            message.success(res.message || "Email sent successfully");
            setIsModalVisible(false);
            setEmail("");
        } catch (err: any) {
            console.error("Email send error:", err);
            message.error(err.message || "Failed to send email");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button
                size="large"
                style={{ cursor: "pointer", color: "orange" }}
                icon={<MailOutlined />}
                onClick={() => setIsModalVisible(true)}
            >
            </Button>

            <Modal
                title="Send Invoice Email"
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
                centered
            >
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <Input
                        placeholder="Enter recipient email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                    <Button
                        type="primary"
                        icon={<FilePdfOutlined />}
                        onClick={handleSendEmail}
                        disabled={!email}
                        loading={loading}
                    >
                        Send Email with PDF
                    </Button>
                </div>

                {loading && (
                    <div style={{ textAlign: "center", marginTop: 16 }}>
                        <Spin />
                    </div>
                )}
            </Modal>
        </>
    );
};

export default InvoicePrint;
